import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { authAPI, userAPI } from '~/api';
import { WALLET_STORAGE_KEY } from '~/constants';

export type LoginParams = {
  walletAddress: string;
  signature: number[];
  message: string;
};

const login = createAsyncThunk(
  'account/login',
  async ({ walletAddress, signature, message }: LoginParams) => {
    const { accessToken, refreshToken } = await authAPI.login(
      message,
      walletAddress,
      signature,
    );
    return {
      accessToken,
      refreshToken,
      wallet_address: walletAddress,
    };
  },
);

const refresh = createAsyncThunk('account/refresh', async () => {
  const currentRefreshToken = localStorage.getItem('refresh_token') ?? '';
  const { accessToken, refreshToken } =
    await authAPI.refresh(currentRefreshToken);
  const { walletAddress } = await userAPI.getCurrentUser();
  return { accessToken, refreshToken, wallet_address: walletAddress };
});

const logout = createAsyncThunk('account/logout', async () => {
  const access_token = await authAPI.logout();
  return { access_token };
});

export type AccountState = {
  loading: boolean;
  isAuth: boolean;
  activated: boolean;
  wallet_address: string;
  access_token: string;
  refresh_token: string;
};

const initialState: AccountState = {
  activated: Boolean(localStorage.getItem('activated')) || false,
  loading: true,
  isAuth: false,
  wallet_address: '',
  access_token: localStorage.getItem('access_token') ?? '',
  refresh_token: localStorage.getItem('refresh_token') ?? '',
};

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setWalletAddress: (state, action: PayloadAction<string>) => {
      state.wallet_address = action.payload;
    },
    setActivated: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem('activated', String(true));
      state.activated = action.payload;
    },
    setAuthDate: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
      }>,
    ) => {
      state.loading = false;
      state.isAuth = true;
      state.access_token = action.payload.accessToken;
      state.refresh_token = action.payload.refreshToken;

      localStorage.setItem('access_token', action.payload.accessToken);
      localStorage.setItem('refresh_token', action.payload.refreshToken);
    },
  },
  extraReducers(builder) {
    // login
    builder.addCase(login.fulfilled, (state, action) => {
      state.isAuth = true;
      state.wallet_address = action.payload.wallet_address;
      state.access_token = action.payload.accessToken;
      state.refresh_token = action.payload.refreshToken;
      localStorage.setItem('access_token', action.payload.accessToken);
      localStorage.setItem('refresh_token', action.payload.refreshToken);
    });
    builder.addCase(login.rejected, (state) => {
      state.isAuth = false;
      state.wallet_address = '';
      state.access_token = '';
      state.refresh_token = '';
    });

    // refresh
    builder.addCase(refresh.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuth = true;
      state.access_token = action.payload.accessToken;
      state.refresh_token = action.payload.refreshToken;
      state.wallet_address = action.payload.wallet_address;

      localStorage.setItem('access_token', action.payload.accessToken);
      localStorage.setItem('refresh_token', action.payload.refreshToken);
    });
    builder.addCase(refresh.rejected, (state) => {
      state.loading = false;
      state.isAuth = false;
      state.wallet_address = '';
      state.access_token = '';
      state.refresh_token = '';

      localStorage.setItem('access_token', '');
      localStorage.setItem('refresh_token', '');
      localStorage.removeItem(WALLET_STORAGE_KEY);
    });

    // logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuth = false;
      state.wallet_address = '';
      state.access_token = '';
      state.refresh_token = '';

      localStorage.setItem('access_token', '');
      localStorage.setItem('refresh_token', '');
      localStorage.removeItem(WALLET_STORAGE_KEY);
    });
    builder.addCase(logout.rejected, (state) => {
      state.isAuth = false;
      state.wallet_address = '';
      state.access_token = '';
      state.refresh_token = '';

      localStorage.setItem('access_token', '');
      localStorage.setItem('refresh_token', '');
      localStorage.removeItem(WALLET_STORAGE_KEY);
    });
  },
  selectors: {
    activated: (state) => state.activated,
    loading: (state) => state.loading,
    isAuth: (state) => state.isAuth,
    walletAddress: (state) => state.wallet_address,
    accessToken: (state) => state.access_token,
  },
});

export const {
  actions: accountActions,
  reducer: accountReducer,
  selectors: accountSelectors,
} = accountSlice;

export const accountThunks = {
  login,
  refresh,
  logout,
};
